import { useCallback, useEffect, useLayoutEffect, useRef, useState, type RefObject, type SetStateAction } from 'react';
import { useLocation } from 'react-router';

const STORAGE_KEY = 'anidex:page-state';
const MAX_ENTRIES = 400;

let store: Map<string, unknown> | null = null;
let saveTimer: ReturnType<typeof setTimeout> | null = null;

function entries(): Map<string, unknown> {
  if (store) return store;
  store = new Map();
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) for (const [id, value] of JSON.parse(raw) as Array<[string, unknown]>) store.set(id, value);
  } catch {
    
  }
  return store;
}

function remember(id: string, value: unknown) {
  const map = entries();
  map.delete(id);
  map.set(id, value);
  while (map.size > MAX_ENTRIES) map.delete(map.keys().next().value as string);
  if (saveTimer) return;
  saveTimer = setTimeout(() => {
    saveTimer = null;
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify([...map]));
    } catch {
      
    }
  }, 150);
}


export function usePageState<T>(name: string | null, initial: T | (() => T)) {
  const { key } = useLocation();
  const id = name === null ? null : `${key}:${name}`;
  const [entry, setEntry] = useState(() => ({
    id,
    value: id !== null && entries().has(id)
      ? (entries().get(id) as T)
      : initial instanceof Function ? initial() : initial,
  }));

  let current = entry;
  if (entry.id !== id) {
    current = { id, value: id !== null && entries().has(id) ? (entries().get(id) as T) : entry.value };
    setEntry(current);
  }

  useEffect(() => {
    if (entry.id !== null) remember(entry.id, entry.value);
  }, [entry]);

  const setValue = useCallback((next: SetStateAction<T>) => {
    setEntry((prev) => ({
      id: prev.id,
      value: next instanceof Function ? next(prev.value) : next,
    }));
  }, []);

  return [current.value, setValue] as const;
}


export function usePageScrollRestoration(ref: RefObject<HTMLElement | null>, name: string) {
  const { key, pathname } = useLocation();
  const previousPath = useRef(pathname);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;
    const id = `${key}:${name}:${pathname}`;
    const saved = entries().get(id);
    const target = typeof saved === 'number' && Number.isFinite(saved) && saved >= 0
      ? saved
      : previousPath.current === pathname ? element.scrollTop : 0;
    previousPath.current = pathname;
    let last = target;
    let restoring = true;
    const resize = new ResizeObserver(() => restore());
    const mutation = new MutationObserver(() => observeContent());

    const stopRestoring = () => {
      restoring = false;
      resize.disconnect();
      mutation.disconnect();
    };

    const restore = () => {
      if (!restoring) return;
      element.scrollTop = target;
      if (Math.abs(element.scrollTop - target) < 1) {
        stopRestoring();
        last = element.scrollTop;
        remember(id, last);
      }
    };

    const observeContent = () => {
      if (!restoring) return;
      resize.disconnect();
      for (const child of element.children) resize.observe(child);
      restore();
    };

    const onScroll = () => {
      if (restoring) return;
      last = element.scrollTop;
      remember(id, last);
    };
    const cancelRestoring = () => {
      if (!restoring) return;
      stopRestoring();
      onScroll();
    };
    const inputEvents = ['wheel', 'touchstart', 'pointerdown', 'keydown'] as const;
    element.addEventListener('scroll', onScroll, { passive: true });
    for (const type of inputEvents) element.addEventListener(type, cancelRestoring, { passive: true });
    mutation.observe(element, { childList: true });
    observeContent();

    return () => {
      stopRestoring();
      element.removeEventListener('scroll', onScroll);
      for (const type of inputEvents) element.removeEventListener(type, cancelRestoring);
      remember(id, last);
    };
  }, [key, pathname, name, ref]);
}
